import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../lib/api";
import { Card, EmptyState, PageHeader, Select, Spinner, StatCard, Badge } from "../components/ui";
import { fmtPct } from "../lib/format";

type DashboardData = {
  metrics: {
    seedAccounts: { total: number; active: number; gmail: number; googleWorkspace: number; microsoft365: number; outlookHotmail: number; yahoo: number; other: number };
    sendingAccounts: { total: number; monitored: number };
    tests: { today: number; week: number };
    placement: { inboxPct: number; spamPct: number; categoryPct: number; missingPct: number; deliverabilityScore: number | null; inboxCount: number; spamCount: number; categoryCount: number; missingCount: number; total: number };
  };
  trend: { date: string; inboxPct: number; spamPct: number }[];
  providerTrend: Record<string, unknown>[];
};

export function Dashboard() {
  const [range, setRange] = useState("7d");
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", range],
    queryFn: () => api<DashboardData>(`/dashboard?range=${range}`),
  });

  if (isLoading) return <Spinner label="Loading dashboard…" />;
  if (!data) return <EmptyState title="No dashboard data" />;

  const m = data.metrics;
  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Deliverability overview across your seed and sending accounts"
        actions={
          <Select value={range} onChange={(e) => setRange(e.target.value)} className="w-40">
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </Select>
        }
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard label="Total Seed Accounts" value={m.seedAccounts.total} sub={`${m.seedAccounts.active} active`} tone="brand" />
        <StatCard label="Gmail Seeds" value={m.seedAccounts.gmail + m.seedAccounts.googleWorkspace} sub={`${m.seedAccounts.gmail} personal · ${m.seedAccounts.googleWorkspace} workspace`} />
        <StatCard label="Microsoft Seeds" value={m.seedAccounts.microsoft365 + m.seedAccounts.outlookHotmail} sub={`${m.seedAccounts.microsoft365} M365 · ${m.seedAccounts.outlookHotmail} Outlook/Hotmail`} />
        <StatCard label="Yahoo Seeds" value={m.seedAccounts.yahoo} sub={`${m.seedAccounts.other} other providers`} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard label="Sending Accounts" value={m.sendingAccounts.total} sub={`${m.sendingAccounts.monitored} monitored`} />
        <StatCard label="Tests Today" value={m.tests.today} />
        <StatCard label="Tests This Week" value={m.tests.week} />
        <StatCard
          label="Deliverability Score"
          value={m.placement.deliverabilityScore ?? "—"}
          sub={m.placement.deliverabilityScore !== null ? <Badge tone={m.placement.deliverabilityScore >= 85 ? "success" : m.placement.deliverabilityScore >= 70 ? "warning" : "danger"}>{m.placement.deliverabilityScore >= 85 ? "Excellent" : m.placement.deliverabilityScore >= 70 ? "Good" : "Needs attention"}</Badge> : "No completed runs"}
          tone={m.placement.deliverabilityScore !== null && m.placement.deliverabilityScore >= 70 ? "success" : "default"}
        />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Inbox" value={`${fmtPct(m.placement.inboxPct)}`} sub={`${m.placement.inboxCount} / ${m.placement.total} messages`} tone="success" />
        <StatCard label="Category" value={`${fmtPct(m.placement.categoryPct)}`} sub={`${m.placement.categoryCount} / ${m.placement.total}`} tone="warning" />
        <StatCard label="Spam" value={`${fmtPct(m.placement.spamPct)}`} sub={`${m.placement.spamCount} / ${m.placement.total}`} tone="danger" />
        <StatCard label="Missing" value={`${fmtPct(m.placement.missingPct)}`} sub={`${m.placement.missingCount} / ${m.placement.total}`} />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card title="Inbox Placement Trend" subtitle="Daily inbox and spam placement across completed runs">
          {data.trend.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.trend}>
                <defs>
                  <linearGradient id="inbox" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip />
                <Area type="monotone" dataKey="inboxPct" name="Inbox %" stroke="#2563eb" fill="url(#inbox)" strokeWidth={2} />
                <Line type="monotone" dataKey="spamPct" name="Spam %" stroke="#ef4444" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState title="No placement data yet" description="Launch an inbox placement test to start building trends." action={<Link to="/tests/new" className="text-sm font-medium text-brand-600 hover:text-brand-700">Create a test →</Link>} />
          )}
        </Card>
        <Card title="Placement by Provider" subtitle="Inbox rate per provider over time">
          {data.providerTrend.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.providerTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip />
                <Line type="monotone" dataKey="gmail" name="Gmail" stroke="#16a34a" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="google_workspace" name="Google Workspace" stroke="#2563eb" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="microsoft_365" name="Microsoft 365" stroke="#ea580c" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="outlook" name="Outlook" stroke="#7c3aed" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="yahoo" name="Yahoo" stroke="#ca8a04" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState title="No provider trend yet" description="Provider placement history appears once tests complete." />
          )}
        </Card>
      </div>
    </div>
  );
}
